import React, { useEffect, useState } from 'react';
import { Link2, Link2Off, Pencil, Briefcase, RadioTower, Check } from 'lucide-react';
import { Job, JobStatus, Site } from '../../../types/site';
import { StockRequest, useStockRequestStore } from '../../../store/stockRequestStore';
import { useProjectStore } from '../../../store/projectStore';

interface Theme {
  border: string;
  text: string;
  muted: string;
  inputBg: string;
  subTableHeaderBg: string;
}

interface Props {
  request: StockRequest;
  sites: Site[];
  canEdit: boolean;
  theme: Theme;
  onUpdated?: () => void;
}

function resolveJob(sites: Site[], siteId: string | null, jobId: string | null): Job | null {
  if (!siteId || !jobId) return null;
  const site = sites.find(s => s.id === siteId);
  return site?.jobs?.find(j => j.id === jobId) ?? null;
}

function resolveJobTitle(sites: Site[], siteId: string | null, jobId: string | null): string | null {
  return resolveJob(sites, siteId, jobId)?.title ?? null;
}

const jobStatusLabel = (status: JobStatus): string => {
  switch (status) {
    case 'in-progress': return 'In Progress';
    case 'pending': return 'Pending';
    case 'completed': return 'Completed';
    case 'cancelled': return 'Cancelled';
    default: return status;
  }
};

const jobStatusBadgeClass = (status: JobStatus, isDark: boolean): string => {
  switch (status) {
    case 'pending':
      return isDark ? 'bg-yellow-900/40 text-yellow-400' : 'bg-yellow-100 text-yellow-700';
    case 'in-progress':
      return isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700';
    case 'completed':
      return isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700';
    case 'cancelled':
      return isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500';
    default:
      return isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500';
  }
};

const selectCls = 'text-sm px-2.5 py-1.5 rounded border w-full focus:outline-none focus:ring-1 focus:ring-blue-500';
const badgeCls =
  'inline-flex h-5 max-h-5 items-center px-2 rounded text-xs font-medium whitespace-nowrap leading-none box-border flex-shrink-0';

const RequestJobLinkBar: React.FC<Props> = ({ request, sites, canEdit, theme, onUpdated }) => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';
  const updateRequestLinks = useStockRequestStore(s => s.updateRequestLinks);
  const [editing, setEditing] = useState(false);
  const [draftSiteId, setDraftSiteId] = useState(request.siteId ?? '');
  const [draftJobId, setDraftJobId] = useState(request.jobId ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!editing) {
      setDraftSiteId(request.siteId ?? '');
      setDraftJobId(request.jobId ?? '');
    }
  }, [request.siteId, request.jobId, editing]);

  const siteName = request.siteId ? sites.find(s => s.id === request.siteId)?.name : null;
  const linkedJob = resolveJob(sites, request.siteId, request.jobId);
  const draftJobs = draftSiteId ? (sites.find(s => s.id === draftSiteId)?.jobs ?? []) : [];
  const hasLink = !!(request.jobId || request.siteId);
  const showActionRail = canEdit && !editing;

  const startEdit = () => {
    setDraftSiteId(request.siteId ?? '');
    setDraftJobId(request.jobId ?? '');
    setError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError('');
  };

  const saveLinks = async () => {
    setSaving(true);
    setError('');
    const ok = await updateRequestLinks(request.id, {
      siteId: draftSiteId || null,
      jobId: draftJobId || null,
    });
    setSaving(false);
    if (!ok) {
      setError(useStockRequestStore.getState().error ?? 'Could not save links.');
      return;
    }
    setEditing(false);
    onUpdated?.();
  };

  const unlinkJob = async () => {
    setSaving(true);
    setError('');
    const ok = await updateRequestLinks(request.id, { jobId: null, siteId: null });
    setSaving(false);
    if (!ok) {
      setError(useStockRequestStore.getState().error ?? 'Could not unlink job.');
      return;
    }
    onUpdated?.();
  };

  const openLinkedJob = () => {
    if (!linkedJob) return;
    window.dispatchEvent(new CustomEvent('openJobFromNotification', {
      detail: { siteId: request.siteId ?? undefined, jobId: linkedJob.id },
    }));
  };

  const btnPrimary =
    'text-sm px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50';
  const btnSecondary =
    'text-sm px-2.5 py-1 rounded border transition-colors disabled:opacity-50';

  const railBtnCls =
    'flex-1 w-full inline-flex items-center justify-center transition-colors disabled:opacity-40';
  const railIcon = isDark ? '#9ca3af' : '#6b7280';
  const editHoverBg = isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe';
  const editHoverIcon = isDark ? '#93c5fd' : '#2563eb';
  const unlinkHoverBg = isDark ? 'rgba(239,68,68,0.2)' : '#fee2e2';
  const unlinkHoverIcon = isDark ? '#fca5a5' : '#dc2626';

  return (
    <div
      className="rounded overflow-hidden flex"
      style={{
        backgroundColor: theme.subTableHeaderBg,
        border: `1px solid ${theme.border}`,
        color: theme.text,
      }}
    >
      <div className={`min-w-0 flex-1 px-3 py-2.5 ${editing ? 'space-y-2' : ''}`}>
        <div className="flex items-center gap-1.5 mb-1.5">
          <Briefcase size={12} style={{ color: theme.muted }} />
          <p className="text-[11px] uppercase tracking-wide font-medium" style={{ color: theme.muted }}>
            Linked Job
          </p>
        </div>

        {!editing && (
          linkedJob ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                <button
                  type="button"
                  onClick={openLinkedJob}
                  className="text-sm font-medium truncate hover:underline underline-offset-2 transition-colors text-left"
                  style={{ color: isDark ? '#93c5fd' : '#2563eb' }}
                  title="Open job"
                >
                  {linkedJob.title}
                </button>
                <span className={`${badgeCls} gap-1 ${jobStatusBadgeClass(linkedJob.status, isDark)}`}>
                  {linkedJob.status === 'completed' && <Check size={11} className="flex-shrink-0" strokeWidth={2.5} />}
                  {jobStatusLabel(linkedJob.status)}
                </span>
              </div>
              {siteName && (
                <span
                  className={`${badgeCls} gap-1 !rounded-full`}
                  style={{
                    backgroundColor: isDark ? 'rgba(99,102,241,0.25)' : '#e0e7ff',
                    color: isDark ? '#a5b4fc' : '#4338ca',
                  }}
                  title="Site"
                >
                  <RadioTower size={11} className="flex-shrink-0" />
                  {siteName}
                </span>
              )}
            </div>
          ) : siteName ? (
            <div className="space-y-1.5">
              <p className="text-sm italic" style={{ color: theme.muted }}>No job linked</p>
              <span
                className={`${badgeCls} gap-1 !rounded-full`}
                style={{
                  backgroundColor: isDark ? 'rgba(99,102,241,0.25)' : '#e0e7ff',
                  color: isDark ? '#a5b4fc' : '#4338ca',
                }}
              >
                <RadioTower size={11} className="flex-shrink-0" />
                {siteName}
              </span>
            </div>
          ) : (
            <p className="text-sm italic" style={{ color: theme.muted }}>
              No site or job linked
            </p>
          )
        )}

        {editing && (
          <div className="space-y-2 pt-0.5">
            <div className="space-y-2">
              <div>
                <label className="text-xs block mb-1" style={{ color: theme.muted }}>Site</label>
                <select
                  value={draftSiteId}
                  onChange={e => { setDraftSiteId(e.target.value); setDraftJobId(''); }}
                  className={selectCls}
                  style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                >
                  <option value="">None</option>
                  {sites.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs block mb-1" style={{ color: theme.muted }}>Job</label>
                <select
                  value={draftJobId}
                  onChange={e => setDraftJobId(e.target.value)}
                  disabled={!draftSiteId || draftJobs.length === 0}
                  className={selectCls}
                  style={{
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: draftJobId ? theme.text : theme.muted,
                  }}
                >
                  <option value="">
                    {draftSiteId ? (draftJobs.length ? 'Select job…' : 'No jobs at site') : 'Select site first'}
                  </option>
                  {draftJobs.map(j => (
                    <option key={j.id} value={j.id}>{j.title}</option>
                  ))}
                </select>
              </div>
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => void saveLinks()} disabled={saving} className={btnPrimary}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className={btnSecondary}
                style={{ borderColor: theme.border, color: theme.text }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {showActionRail && (
        <div
          className="flex flex-col flex-shrink-0 w-8"
          style={{ borderLeft: `1px solid ${theme.border}` }}
        >
          <button
            type="button"
            onClick={startEdit}
            disabled={saving}
            className={railBtnCls}
            style={{ color: railIcon }}
            title={hasLink ? 'Change linked job' : 'Link job'}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = editHoverBg;
              e.currentTarget.style.color = editHoverIcon;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = railIcon;
            }}
          >
            {hasLink ? <Pencil size={13} /> : <Link2 size={13} />}
          </button>
          {hasLink && (
            <button
              type="button"
              onClick={() => void unlinkJob()}
              disabled={saving}
              className={railBtnCls}
              style={{
                color: railIcon,
                borderTop: `1px solid ${theme.border}`,
              }}
              title="Unlink job"
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = unlinkHoverBg;
                e.currentTarget.style.color = unlinkHoverIcon;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = railIcon;
              }}
            >
              <Link2Off size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RequestJobLinkBar;
export { resolveJobTitle };
